/* eslint-disable */
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

/* ------------------ Types ------------------ */

export type Approver = {
  id: string;
  name: string;
};

export type Approval = {
  role: string;
  status: string;
  approver: Approver;
};

export type LeaveHistoryEntry = {
  leaveType: string;
  fromDate: string;
  toDate: string;
  numberOfDays?: number;
  deductLeave?: number;
  lateMinutes?: number;
  status: string;
  approvals?: Approval[];
  createdAt?: string;
};

/**
 * Backend structure:
 * allocatedBreakdown: { annualLeave: 0, sickLeave: 0, ... }
 */
export type AllocatedBreakdown = {
  [leaveType: string]: number;
};

export type UserLeaveBalance = {
  userId: string;
  startDate?: string | null;
  firstName: string;
  lastName: string;
  departmentId?: string;
  department?: string;

  totalAllocated: number;
  totalUsed: number;
  totalPending: number;
  totalBalance: number;

  leaveWithoutPayUsed?: number;
  carryForward?: string | number | null;

  /** ✅ NEW (matches backend exactly) */
  allocatedBreakdown?: AllocatedBreakdown;

  /** legacy / optional (keep only if some APIs still return it) */
  leaveBreakdown?: any;

  leaveHistory?: LeaveHistoryEntry[];
};

export type LeaveBalanceSummaryResponse = {
  success: boolean;
  year?: number;
  count?: number;
  data: UserLeaveBalance[];
  message?: string;
};

export type LeaveBalanceQueryParams = {
  userName?: string;
  startDateFrom?: string;
  startDateTo?: string;
  departmentName?: string;
};

/* ------------------ Helpers ------------------ */

const buildQueryString = (params?: LeaveBalanceQueryParams): string => {
  if (!params) return "";
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });

  const result = query.toString();
  return result ? `?${result}` : "";
};

/* ------------------ API ------------------ */

const fetchLeaveBalanceSummary = async (
  orgId: string,
  params?: LeaveBalanceQueryParams,
): Promise<LeaveBalanceSummaryResponse> => {
  if (!orgId) throw new Error("Organization ID is required.");

  const qs = buildQueryString(params);
  const url = `/leaves/leave-balance-summary/${orgId}${qs}`;

  const response = await customAxios.get<LeaveBalanceSummaryResponse>(url);
  return response.data;
};

/* ------------------ Hook ------------------ */

export const useGetLeaveBalanceSummary = (
  orgId: string,
  params?: LeaveBalanceQueryParams,
  enabled = true,
) =>
  useQuery<LeaveBalanceSummaryResponse, AxiosError>(
    ["leaveBalanceSummary", orgId, params ?? {}],
    () => fetchLeaveBalanceSummary(orgId, params),
    {
      enabled: Boolean(orgId) && enabled,
      retry: 1,
      staleTime: 0,
      refetchOnMount: "always",
      onError: (error) => {
        const message =
          (error.response?.data as { message?: string })?.message ??
          error.message ??
          "Failed to load leave balance summary.";

        showNotification({
          title: "Error Loading Leave Balance Summary",
          message,
          color: "red",
        });
      },
    },
  );
